import React from 'react';
import {
  SkillNode, SkillTreeData, PassiveAbilityDefinition, SkillEffectData,
  findPassiveDefinition, humanizeEffectKey, formatBonusValue,
} from '../types/skillTree';

interface Props {
  selectedSkill: SkillNode;
  skillTreeData: SkillTreeData;
  passiveDefinitions: PassiveAbilityDefinition[];
  learningSkill: number | null;
  onLearnSkill: (skillNodeId: number) => void;
  parseSkillEffects: (json: string) => SkillEffectData | null;
}

const SkillDetailsPanel: React.FC<Props> = ({
  selectedSkill, skillTreeData, passiveDefinitions, learningSkill, onLearnSkill, parseSkillEffects,
}) => {
  const effects = parseSkillEffects(selectedSkill.skillEffects);
  const passiveAbility = selectedSkill.isLearned && effects?.PassiveAbilities?.[0]
    ? findPassiveDefinition(passiveDefinitions, effects.PassiveAbilities[0])
    : null;
  const learnedSkill = selectedSkill.isLearned
    ? skillTreeData.learnedSkills.find(ls => ls.skillNodeId === selectedSkill.id)
    : null;

  return (
    <div className="skill-details-panel">
      <div className="skill-details-title">{selectedSkill.name}</div>
      <div className="skill-details-description">{selectedSkill.description}</div>

      <div className="skill-info">
        <div className="skill-effect-item">
          <span className="skill-effect-label">Category:</span>
          <span className="skill-effect-value">{selectedSkill.skillCategory}</span>
        </div>
        <div className="skill-effect-item">
          <span className="skill-effect-label">Required Level:</span>
          <span className="skill-effect-value">{selectedSkill.requiredLevel}</span>
        </div>
        <div className="skill-effect-item">
          <span className="skill-effect-label">Talent Cost:</span>
          <span className="skill-effect-value">{selectedSkill.talentPointCost}</span>
        </div>
        {learnedSkill && learnedSkill.skillLevel > 0 && (
          <>
            <div className="skill-effect-item">
              <span className="skill-effect-label">Skill Level:</span>
              <span className="skill-effect-value skill-level-highlight">Level {learnedSkill.skillLevel}</span>
            </div>
            <div className="skill-effect-item">
              <span className="skill-effect-label">Skill XP:</span>
              <span className="skill-effect-value">{learnedSkill.skillXP} / {learnedSkill.xpToNextLevel}</span>
            </div>
            <div className="skill-xp-bar">
              <div
                className="skill-xp-progress"
                style={{ width: `${(learnedSkill.skillXP / learnedSkill.xpToNextLevel) * 100}%` }}
              />
            </div>
            <div className="skill-effect-item">
              <span className="skill-effect-label">Times Used:</span>
              <span className="skill-effect-value">{learnedSkill.timesUsed}</span>
            </div>
          </>
        )}
      </div>

      {effects && (
        <div className="skill-effects">
          <div className="skill-effects-title">Effects:</div>
          {effects.StatBonuses && Object.entries(effects.StatBonuses).map(([stat, value]) =>
            value > 0 && (
              <div key={stat} className="skill-effect-item">
                <span className="skill-effect-label">{humanizeEffectKey(stat)}:</span>
                <span className="skill-effect-value">+{value}</span>
              </div>
            )
          )}
          {effects.CombatBonuses && Object.entries(effects.CombatBonuses).map(([bonus, value]) =>
            value > 0 && (
              <div key={bonus} className="skill-effect-item">
                <span className="skill-effect-label">{humanizeEffectKey(bonus)}:</span>
                <span className="skill-effect-value">{formatBonusValue(bonus, value)}</span>
              </div>
            )
          )}
          {effects.PassiveAbilities && effects.PassiveAbilities.map(ability => {
            const abilityDef = findPassiveDefinition(passiveDefinitions, ability);
            return (
              <div key={ability} className="skill-effect-item">
                <span className="skill-effect-label">Passive:</span>
                <span className="skill-effect-value" title={abilityDef?.description}>
                  {abilityDef?.name ?? ability}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {passiveAbility && (
        <div className="passive-ability-details">
          <div className="skill-effects-title">Passive Ability:</div>
          <div className="skill-details-description">{passiveAbility.description}</div>
        </div>
      )}

      {selectedSkill.canLearn && !selectedSkill.isLearned && (
        <button
          className="learn-skill-button"
          onClick={() => onLearnSkill(selectedSkill.id)}
          disabled={learningSkill === selectedSkill.id}
        >
          {learningSkill === selectedSkill.id
            ? 'Learning...'
            : `Learn Skill (${selectedSkill.talentPointCost} points)`}
        </button>
      )}

      {selectedSkill.cannotLearnReason && (
        <div className="error-message">{selectedSkill.cannotLearnReason}</div>
      )}
    </div>
  );
};

export default SkillDetailsPanel;
